import React from 'react';
import PropTypes from 'prop-types';

import DraftMessage from '../../correspondances/DraftMessage';

const EmailResponseView = (props) => {
  const {communicationTarget, isRegenerating, onApproveAndSendSuccess, onDiscard, onRegenerate, requester, response} = props;

  const [isSendingEmail, setIsSendingEmail] = React.useState(false);
  const [emailData, setEmailData] = React.useState(() => {
    return {
      subject: response.subject,
      message: response.content,
      fromName: [requester?.firstName, requester?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim(),
      fromEmail: requester.email,
      delayUntil: 0,
    };
  });
  const [selectedSenderUser, setSelectedSenderUser] = React.useState(requester);
  const relationships = communicationTarget?.relationships || [];
  const [selectedRelationship, setSelectedRelationship] = React.useState(communicationTarget?.defaultRelationship || null);
  const recipientEmail = communicationTarget?.getRelationshipEmail?.(selectedRelationship) || '';

  const updateEmailData = (key) => (value) => {
    setEmailData((prevEmailData) => ({
      ...prevEmailData,
      [key]: value,
    }));
  };

  React.useEffect(() => {
    setEmailData({
      subject: response.subject,
      message: response.content,
      fromName: [requester?.firstName, requester?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim(),
      fromEmail: requester.email,
      delayUntil: 0,
    });
    setSelectedSenderUser(requester);
  }, [requester, response]);

  React.useEffect(() => {
    if (!selectedSenderUser) {
      return;
    }

    const selectedSenderName = `${selectedSenderUser?.firstName || ''} ${selectedSenderUser?.lastName || ''}`.trim();

    if (
      (emailData.fromName ?? '') !== selectedSenderName ||
      (emailData.fromEmail ?? '') !== (selectedSenderUser.email ?? '')
    ) {
      setSelectedSenderUser(null);
    }
  }, [emailData.fromEmail, emailData.fromName, selectedSenderUser]);

  const handleSenderUserSelect = (senderUser) => {
    setSelectedSenderUser(senderUser);

    if (!senderUser) {
      return;
    }

    setEmailData((prevEmailData) => ({
      ...prevEmailData,
      fromName: `${senderUser?.firstName || ''} ${senderUser?.lastName || ''}`.trim(),
      fromEmail: senderUser.email || '',
    }));
  };

  const isHtmlEmailBody = /<\/?[a-z][\s\S]*>/i.test(emailData.message ?? '');
  const canApproveAndSend = Boolean(recipientEmail) && Boolean(communicationTarget?.sendEmail);

  const handleApproveAndSend = React.useCallback(async () => {
    if (!canApproveAndSend) {
      return;
    }

    setIsSendingEmail(true);

    try {
      await communicationTarget.sendEmail(emailData, selectedRelationship?.relationshipRn);
      await onApproveAndSendSuccess?.();
      onDiscard?.();
    } finally {
      setIsSendingEmail(false);
    }
  }, [canApproveAndSend, communicationTarget, emailData, onApproveAndSendSuccess, onDiscard, selectedRelationship?.relationshipRn]);

  const fields = [
    {
      key: 'to',
      type: 'relationshipAutocomplete',
      expression: 'skills.SkillsModule.Email.Fields.To',
      getOptionDetail: communicationTarget.getRelationshipEmail,
      options: relationships,
      selectedRelationship,
      setSelectedRelationship,
      autocompleteSx: {
        '& .MuiAutocomplete-endAdornment': {
          right: 0,
        },
      },
    },
    {
      key: 'fromName',
      type: 'adminAutocomplete',
      expression: 'skills.SkillsModule.Email.Fields.From',
      selectedAdmin: selectedSenderUser,
      selectedDisplayValue: emailData.fromName,
      setSelectedAdmin: handleSenderUserSelect,
      showEmail: true,
    },
    {
      key: 'subject',
      type: 'editable',
      expression: 'skills.SkillsModule.Email.Fields.Subject',
      value: emailData.subject,
      onChange: updateEmailData('subject'),
    },
  ];

  const actions = [
    {
      key: 'approve',
      variant: 'primary',
      expression: 'skills.SkillsModule.Email.Actions.ApproveAndSend',
      disabled: isSendingEmail || !canApproveAndSend,
      onClick: handleApproveAndSend,
    },
    {
      key: 'regenerate',
      variant: 'secondary',
      expression: 'skills.SkillsModule.Email.Actions.Regenerate',
      disabled: isRegenerating,
      onClick: onRegenerate,
    },
    {
      key: 'discard',
      variant: 'tertiary',
      expression: 'skills.SkillsModule.Email.Actions.Discard',
      onClick: onDiscard,
    },
  ];

  return (
    <DraftMessage
      titleExpression='skills.SkillsModule.Email.Draft'
      fields={fields}
      bodyField={{
        value: emailData.message,
        onChange: updateEmailData('message'),
        isHtml: isHtmlEmailBody,
      }}
      actions={actions}
    />
  );
};

EmailResponseView.propTypes = {
  communicationTarget: PropTypes.object,
  isRegenerating: PropTypes.bool,
  onApproveAndSendSuccess: PropTypes.func,
  onDiscard: PropTypes.func,
  onRegenerate: PropTypes.func,
  requester: PropTypes.object.isRequired,
  response: PropTypes.shape({
    content: PropTypes.string,
    message: PropTypes.string,
    subject: PropTypes.string,
    text: PropTypes.string,
  }),
};

export default React.memo(EmailResponseView);
